import React, { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError, formatDate } from "./api";
import { useAdmin } from "./AdminContext";

const inputCls = "w-full border border-ivory/20 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-ivory focus:outline-none";

export default function AdminTeam() {
  const { user: me } = useAdmin();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  const load = useCallback(() => {
    adminApi.get("/users").then((r) => setUsers(r.data.results)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi.post("/users", form);
      toast.success(`Account created for ${form.name}.`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    const action = u.active ? "revoke access for" : "restore access for";
    if (!window.confirm(`Are you sure you want to ${action} ${u.name}?`)) return;
    try {
      await adminApi.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Access revoked." : "Access restored.");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset the password for ${u.name}?`)) return;
    try {
      const { data } = await adminApi.post(`/users/${u.id}/reset-password`);
      setTempPassword({ name: u.name, password: data.temporary_password });
      toast.success("Temporary password created.");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="admin-team">
      <h1 className="font-serif text-3xl text-ivory sm:text-4xl">Team &amp; Access</h1>
      <p className="mt-2 max-w-2xl text-sm text-ivory/60">Only you (the owner) can see this page. Create accounts for your PA, reset passwords, or revoke access at any time.</p>

      {tempPassword && (
        <div className="mt-6 border border-amber-300/40 bg-amber-300/10 p-6" data-testid="temp-password-box">
          <p className="text-sm text-ivory">Temporary password for <strong>{tempPassword.name}</strong>:</p>
          <p className="mt-2 font-mono text-lg text-ivory" data-testid="temp-password-value">{tempPassword.password}</p>
          <p className="mt-2 text-xs text-ivory/60">Share this once, privately. Ask them to change it from Dashboard → My Account after signing in.</p>
          <button onClick={() => setTempPassword(null)} className="mt-3 text-xs font-semibold uppercase tracking-[0.15em] text-ivory/70 underline underline-offset-4">I've noted it down</button>
        </div>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-3">
        <div className="border border-ivory/10 bg-[#242424] p-6 lg:col-span-2" data-testid="team-list">
          <h2 className="font-serif text-2xl text-ivory">Accounts</h2>
          {!users ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-ivory/50" /></div>
          ) : (
            <ul className="mt-4 divide-y divide-ivory/10">
              {users.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`team-user-${u.email}`}>
                  <div>
                    <p className="font-medium text-ivory">{u.name} {u.id === me?.id && <span className="text-ivory/40">(you)</span>}</p>
                    <p className="text-xs text-ivory/50">{u.email} · {u.role === "owner" ? "Owner" : "Property Manager"} · since {formatDate(u.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.15em] ${u.active ? "bg-emerald-400/15 text-emerald-300" : "bg-white/10 text-ivory/50"}`}>
                      {u.active ? "Active" : "Revoked"}
                    </span>
                    {u.role !== "owner" && (
                      <>
                        <button onClick={() => resetPassword(u)} data-testid={`team-reset-${u.id}`} className="flex items-center gap-1.5 border border-ivory/20 px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-ivory/70 hover:bg-white/10">
                          <KeyRound className="h-3 w-3" /> Reset password
                        </button>
                        <button onClick={() => toggleActive(u)} data-testid={`team-toggle-${u.id}`} className="border border-ivory/20 px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-ivory/70 hover:bg-white/10">
                          {u.active ? "Revoke access" : "Restore"}
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-ivory/10 bg-[#242424] p-6" data-testid="team-create">
          <p className="eyebrow flex items-center gap-2 text-ivory/60"><UserPlus className="h-3.5 w-3.5" /> Add a Property Manager</p>
          <form onSubmit={createUser} className="mt-4 space-y-4">
            <input placeholder="Full name" data-testid="team-new-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input type="email" placeholder="Email" data-testid="team-new-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input type="text" placeholder="Temporary password (8+ characters)" data-testid="team-new-password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="off" />
            <button type="submit" disabled={busy} data-testid="team-create-submit" className="flex w-full items-center justify-center gap-2 bg-brand px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ivory hover:bg-brand-dark disabled:opacity-60">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create account
            </button>
            <p className="text-[0.7rem] leading-relaxed text-ivory/45">Property Managers can add, edit and publish listings and manage photos. They cannot manage accounts or delete properties.</p>
          </form>
        </div>
      </div>
    </div>
  );
}
